import React, { useState } from 'react';
import { ScrollText, Filter, User } from 'lucide-react';
import { useLogs, useUserActivity } from '@/hooks/useApi';
import { Card, Badge, LoadingSpinner } from '@/components/UI';
import { formatDateTime } from '@/utils';
import type { LogEntry, UserActivity } from '@/types';

export function LogsPage() {
  const [levelFilter, setLevelFilter] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [tab, setTab] = useState<'system' | 'activity'>('system');

  const { data: logsData, isLoading: logsLoading } = useLogs({
    level: levelFilter || undefined,
    module: moduleFilter || undefined,
    limit: 100,
  });

  const { data: activityData, isLoading: actLoading } = useUserActivity({ limit: 50 });

  const logs = ((logsData as any)?.items ?? logsData ?? []) as LogEntry[];
  const activities = ((activityData as any)?.items ?? activityData ?? []) as UserActivity[];

  const levelVariant = (level: string): 'profit' | 'loss' | 'warning' | 'neutral' => {
    switch (level.toUpperCase()) {
      case 'ERROR': case 'CRITICAL': return 'loss';
      case 'WARNING': return 'warning';
      case 'INFO': return 'profit';
      default: return 'neutral';
    }
  };

  return (
    <div className="space-y-6 animate-in">
      <h1 className="text-2xl font-bold text-surface-100">System Logs</h1>

      {/* Tabs */}
      <div className="flex gap-2">
        <button
          onClick={() => setTab('system')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            tab === 'system' ? 'bg-brand-600 text-white' : 'bg-surface-800 text-surface-400 hover:bg-surface-700'
          }`}
        >
          <ScrollText className="w-4 h-4 inline mr-1.5" /> System Logs
        </button>
        <button
          onClick={() => setTab('activity')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            tab === 'activity' ? 'bg-brand-600 text-white' : 'bg-surface-800 text-surface-400 hover:bg-surface-700'
          }`}
        >
          <User className="w-4 h-4 inline mr-1.5" /> User Activity
        </button>
      </div>

      {tab === 'system' && (
        <>
          {/* Filters */}
          <Card>
            <div className="flex flex-wrap items-center gap-3">
              <Filter className="w-4 h-4 text-surface-500" />
              <select
                className="select w-auto"
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
              >
                <option value="">All Levels</option>
                <option value="DEBUG">Debug</option>
                <option value="INFO">Info</option>
                <option value="WARNING">Warning</option>
                <option value="ERROR">Error</option>
                <option value="CRITICAL">Critical</option>
              </select>
              <input
                className="input w-48"
                placeholder="Module filter"
                value={moduleFilter}
                onChange={(e) => setModuleFilter(e.target.value)}
              />
            </div>
          </Card>

          {/* Log Entries */}
          <Card noPadding>
            {logsLoading ? (
              <div className="p-6"><LoadingSpinner /></div>
            ) : logs.length === 0 ? (
              <div className="p-6 text-center text-sm text-surface-500">No log entries found</div>
            ) : (
              <div className="max-h-[600px] overflow-y-auto font-mono text-xs">
                {logs.map((entry: LogEntry, i: number) => (
                  <div
                    key={i}
                    className={`flex items-start gap-3 px-4 py-2 border-b border-surface-800/30 hover:bg-surface-800/20 ${
                      entry.level === 'ERROR' || entry.level === 'CRITICAL' ? 'bg-loss/5' : ''
                    }`}
                  >
                    <span className="text-surface-600 whitespace-nowrap flex-shrink-0">
                      {formatDateTime(entry.timestamp)}
                    </span>
                    <Badge variant={levelVariant(entry.level)} className="flex-shrink-0">
                      {entry.level}
                    </Badge>
                    <span className="text-surface-500 flex-shrink-0">{entry.module}</span>
                    <span className="text-surface-300 flex-1">{entry.message}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}

      {tab === 'activity' && (
        <Card title="User Activity" subtitle="Recent user actions">
          {actLoading ? (
            <LoadingSpinner />
          ) : activities.length === 0 ? (
            <div className="text-sm text-surface-500 text-center py-8">No activity records</div>
          ) : (
            <div className="space-y-2">
              {activities.map((activity: UserActivity) => (
                <div
                  key={activity.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-surface-800/50"
                >
                  <div className="flex items-center gap-3">
                    <Badge variant="neutral">{activity.action}</Badge>
                    <div>
                      <p className="text-sm text-surface-200">{activity.details}</p>
                      <p className="text-xs text-surface-500">User: {activity.user_id}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-surface-500">{formatDateTime(activity.created_at)}</p>
                    {activity.ip_address && (
                      <p className="text-[10px] text-surface-600">IP: {activity.ip_address}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
